import type { ComponentProps } from "react"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import PortfolioSectionHeader from "@/v2-backoffice-client/portfolio/PortfolioSectionHeader"
import UsageReportCard from "@/v2-backoffice-client/usage/report/UsageReportCard"
import {
  SEAT_BAND_LABELS,
  type UsageSeatBands,
} from "@/v2-backoffice-client/usage/scope/usage.scope.utils"
import { SEAT_BAND_CHART_COLORS } from "@/v2-backoffice-client/portfolio/portfolio.constants"

export type PortfolioEngagementBandsProps = ComponentProps<"section"> & {
  /** Fleet-wide seat counts per engagement band. */
  bands: UsageSeatBands
}

/** Best band on top — the list reads as a health gradient. */
const BAND_ORDER: (keyof UsageSeatBands)[] = [
  "power",
  "healthy",
  "cooling",
  "dormant",
  "quiet",
]

// How the fleet's seats distribute across the engagement bands. Dense HTML
// rows instead of a chart: five numbers don't need axes, and idle licenses
// usually dwarf the rest — the share column keeps the small bands readable
// where a linear bar squashes them.
function PortfolioEngagementBands(props: PortfolioEngagementBandsProps) {
  const { bands, className, ...sectionProps } = props

  const rows = BAND_ORDER.map((band) => ({
    band,
    label: SEAT_BAND_LABELS[band],
    count: bands[band],
    color: SEAT_BAND_CHART_COLORS[band],
  }))
  const total = rows.reduce((sum, row) => sum + row.count, 0)
  const max = Math.max(1, ...rows.map((row) => row.count))

  return (
    <section className={cn("flex flex-col gap-4", className)} {...sectionProps}>
      <PortfolioSectionHeader
        eyebrow="Seats"
        title="Seat engagement"
        lead="Every customer-linked seat, banded by how engaged it was in the last 28 days."
        info="Every customer-linked seat, banded by engagement over the last 28 days. Idle licenses are the renewal risk to work down — the License utilization list below names the accounts to start with."
      >
        <span className="ml-auto text-xs tabular-nums text-muted-foreground">
          {formatNumber(total)} seats
        </span>
      </PortfolioSectionHeader>

      <UsageReportCard className="flex-1">
        <div className="flex flex-col gap-2.5">
          {rows.map((row) => {
            const share = total > 0 ? row.count / total : 0
            return (
              <div key={row.band} className="flex items-center gap-2">
                <span className="w-24 shrink-0 text-xs text-muted-foreground">
                  {row.label}
                </span>
                <div className="h-3.5 min-w-0 flex-1 overflow-hidden rounded-sm bg-muted/40">
                  <div
                    className="h-full rounded-sm"
                    style={{
                      width: `${Math.max(row.count > 0 ? 1 : 0, (row.count / max) * 100)}%`,
                      backgroundColor: row.color,
                    }}
                  />
                </div>
                <span className="w-14 shrink-0 text-right text-xs font-medium tabular-nums">
                  {formatNumber(row.count)}
                </span>
                <span className="w-10 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                  {(share * 100).toFixed(0)}%
                </span>
              </div>
            )
          })}
        </div>
      </UsageReportCard>
    </section>
  )
}

export default PortfolioEngagementBands
